/* Announcement bar elements.
   <announcement-marquee>  endless scrolling row. Repeats the messages until one group is wider than the bar, then
                           sets the animation length from the width and the speed setting (px per second = speed * 1.2333).
   <announcement-slider>   one message at a time with previous / next buttons and optional auto rotation. */

class AnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector('[data-group]');
    this.copy = this.querySelector('[data-group-copy]');
    if (!this.group || !this.copy) return;
    this.originals = [...this.group.querySelectorAll('[data-item]')];
    this.speed = Number(this.dataset.speed) || 25;
    this.build = this.build.bind(this);
    this.resizeObserver = new ResizeObserver(() => { clearTimeout(this.timer); this.timer = setTimeout(this.build, 150); });
    this.resizeObserver.observe(this);
    this.build();
    document.fonts?.ready.then(this.build);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.resizeObserver?.disconnect();
  }

  build() {
    if (!this.isConnected || !this.originals.length) return;
    const available = this.clientWidth;
    if (!available) return;
    this.classList.remove('is-ready');
    // Back to one set of messages, then add copies until the group is wider than the bar.
    this.group.querySelectorAll('[data-clone]').forEach((el) => el.remove());
    const setWidth = this.originals.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
    if (!setWidth) return;
    const copies = Math.max(0, Math.ceil(available / setWidth) - 1);
    for (let i = 0; i < copies; i++) {
      this.originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute('data-item');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        this.group.append(clone);
      });
    }
    // Second group is a plain copy of the first so the loop has no gap.
    this.copy.replaceChildren(...[...this.group.children].map((el) => {
      const c = el.cloneNode(true);
      c.removeAttribute('data-shopify-editor-block');
      c.querySelectorAll('a').forEach((a) => a.replaceWith(Object.assign(document.createElement('span'), { className: a.className, textContent: a.textContent })));
      return c;
    }));
    this.classList.add('is-ready');
    const width = this.group.getBoundingClientRect().width;
    const pixelsPerSecond = Math.max(1, this.speed * 1.2333);
    const duration = width / pixelsPerSecond;
    this.style.setProperty('--marquee-duration', `${duration.toFixed(2)}s`);
    // Live starts the row with a set of messages beginning at the middle of the bar: start the loop at that point.
    const offset = (available / 2) % setWidth;
    const progress = offset > 0 ? (setWidth - offset) / width : 0;
    this.style.setProperty('--marquee-delay', `${(-progress * duration).toFixed(3)}s`);
  }
}
if (!customElements.get('announcement-marquee')) customElements.define('announcement-marquee', AnnouncementMarquee);

class AnnouncementSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));
    ['pointerenter', 'focusin'].forEach((e) => this.addEventListener(e, () => (this.paused = true)));
    ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
    const seconds = Number(this.dataset.autoplay);
    if (seconds > 0 && this.slides.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => { if (!this.paused && !document.hidden) this.go(1); }, seconds * 1000);
    }
    // Theme editor: show the message that is being edited.
    this.onBlockSelect = (event) => {
      const i = this.slides.indexOf(event.target);
      if (i > -1) { this.show(i); this.paused = true; }
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', () => (this.paused = false));
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  go(step) {
    this.show((this.index + step + this.slides.length) % this.slides.length);
  }

  show(i) {
    this.index = i;
    this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
  }
}
if (!customElements.get('announcement-slider')) customElements.define('announcement-slider', AnnouncementSlider);
